import { IconButton, Menu, Tooltip, Typography, Box } from "@mui/material";
import { useCommands, useActive, useRemirrorContext } from "@remirror/react";
import { CommandMenuItem } from "@remirror/react-ui";
import { useState } from "react";

const FONT_SIZES = [8, 9, 10, 12, 14, 18, 24, 30, 36, 48, 60, 72, 96];

// Helper function to parse font size from various formats
// const parseFontSize = (fontSizeValue: string | undefined): number | null => {
//   if (!fontSizeValue) return null;

//   // Remove units and parse the numeric value
//   const numericValue = parseFloat(fontSizeValue.replace(/[^\d.]/g, ""));
//   return isNaN(numericValue) ? null : Math.round(numericValue);
// };

// Helper function to get current font size for display
const getCurrentFontSize = (
  active: ReturnType<typeof useActive>,
  remirrorContext?: ReturnType<typeof useRemirrorContext>
): string | null => {
  // Check if fontSize mark is active
  if (active.fontSize && typeof active.fontSize === "function") {
    const isFontSizeActive = active.fontSize();

    // If fontSize is active, try to get the value from remirror context
    if (
      isFontSizeActive &&
      remirrorContext &&
      remirrorContext.helpers &&
      remirrorContext.helpers.getFontSizeForSelection
    ) {
      try {
        const fontSizeHelper =
          remirrorContext.helpers.getFontSizeForSelection();
        if (fontSizeHelper && fontSizeHelper.length > 0) {
          const [size] = fontSizeHelper[0];
          // Only return if it's not a default browser size
          if (size && size !== 16 && size !== 14) {
            return Math.round(size).toString();
          }
        }
      } catch {
        // Ignore errors and continue
      }
    }
  }

  // Don't return any default values - only return if font size is explicitly set
  return null;
};

export const FontSizeButton = () => {
  const { setFontSize } = useCommands();
  const active = useActive();
  const remirrorContext = useRemirrorContext() as ReturnType<
    typeof useRemirrorContext
  >;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Get current font size for display on button
  const currentFontSize = getCurrentFontSize(active, remirrorContext);
  const displaySize = currentFontSize;

  // Check if current size matches any predefined size for active state
  const currentSizeNum = currentFontSize ? parseInt(currentFontSize) : null;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSizeSelect = (size: string) => {
    setFontSize(size);
    handleClose();
  };

  return (
    <>
      <Tooltip title="Font size">
        <IconButton
          aria-label="Set font size"
          aria-controls={open ? "font-size-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          size="small"
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: `${theme.shape.borderRadius}px`,
            padding: "6px 8px",
            minWidth: "36px",
            height: "30px", // Match exact height of other buttons
            "&:not(:first-of-type)": {
              marginLeft: "-1px",
              borderLeft: "1px solid transparent",
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            },
            "&:not(:last-of-type)": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          })}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {displaySize ? (
              <Typography
                variant="body2"
                sx={{ fontSize: "0.8rem", fontWeight: 500 }}
              >
                {displaySize}
              </Typography>
            ) : (
              <Box sx={{ width: "1rem", height: "1rem" }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M9 4v3h5v12h3V7h5V4H9zm-6 8h3v7h3v-7h3V9H3v3z" />
                </svg>
              </Box>
            )}
            <Box sx={{ width: "1rem", height: "1rem" }}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </Box>
          </Box>
        </IconButton>
      </Tooltip>
      <Menu
        id="font-size-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "font-size-button",
        }}
      >
        {FONT_SIZES.map((s) => {
          const size = "" + s;
          const isActive = currentSizeNum === s;

          return (
            <CommandMenuItem
              key={size}
              commandName="setFontSize"
              onSelect={() => handleSizeSelect(size)}
              enabled={setFontSize.enabled(size)}
              active={isActive}
              label={size}
              icon={null}
              displayDescription={false}
            />
          );
        })}
        {/* Show current size if it's not in the predefined list */}
        {currentFontSize && !FONT_SIZES.includes(currentSizeNum!) && (
          <>
            <CommandMenuItem
              key="divider"
              commandName=""
              enabled={false}
              onSelect={() => {}}
              label="—————————"
              icon={null}
              displayDescription={false}
            />
            <CommandMenuItem
              key={currentFontSize}
              commandName="setFontSize"
              onSelect={() => handleSizeSelect(currentFontSize)}
              enabled={setFontSize.enabled(currentFontSize)}
              active={true}
              label={`${currentFontSize} (current)`}
              icon={null}
              displayDescription={false}
            />
          </>
        )}
      </Menu>
    </>
  );
};
